import { useCallback, useEffect, useState } from 'react'
import { AlertStatus, Button, Icon, useDisclosure } from '@ritualco/jutsu'
import { MdAdd } from 'react-icons/md'
import {
  useCreateIncentivePlan,
  useDeleteIncentivePlan,
  useGetIncentivePlans,
  usePauseIncentivePlan,
  usePublishIncentivePlan,
  useResumeIncentivePlan,
} from 'api/endpoints/incentivePlan'
import { EditableSection } from 'components/EditableSection'
import Loading from 'components/Loading'
import { IncentiveDeleteModal } from 'components/Modals/IncentiveDeleteModal'
import { FullWidthPage } from 'components/PageTemplates'
import { Pagination } from 'components/Pagination'
import { IncentivePlansTable } from 'components/Tables/IncentivePlansTable'
import { INITIAL_ROWS_PER_PAGE } from 'config'
import { useMessage } from 'contexts/MessageContext'
import { useContent } from 'lib/content/react'
import { useHistory } from 'lib/router'
import { useGetPaymentMethods } from '../../api/endpoints/paymentMethod'
import { NoPaymentMethodModal } from '../../components/Modals/NoPaymentMethodModal'

export type IncentivesProps = {
  organizationId: string
  workspaceId: string
  timezone: string
}

const Incentives = ({
  organizationId,
  workspaceId,
  timezone,
}: IncentivesProps) => {
  const history = useHistory()
  const { formatMessage } = useContent()
  const { addMessage } = useMessage()
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure()
  const [deleteTargetId, setDeleteTargetId] = useState('')
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE)
  const [paginationTokenOption, setPaginationTokenOption] = useState({})
  const { data, status } = useGetIncentivePlans(
    {
      organizationId,
      workspaceId,
      limit: rowsPerPage,
      ...paginationTokenOption,
    },
    timezone
  )
  const create = useCreateIncentivePlan()
  const remove = useDeleteIncentivePlan()
  const publish = usePublishIncentivePlan()
  const pause = usePauseIncentivePlan()
  const resume = useResumeIncentivePlan()
  const toast = useCallback(
    (status: AlertStatus, id: string) => {
      addMessage({
        status,
        description: formatMessage({ id }),
        isClosable: false,
      })
    },
    [addMessage, formatMessage]
  )

  const { refetch } = useGetPaymentMethods({
    workspaceId,
  })
  const [showNoPaymentInfo, setShowNoPaymentInfo] = useState<boolean>(false)
  const [launchIncentivePlan, setLaunchIncentivePlan] = useState<
    string | undefined
  >()
  useEffect(() => {
    if (!launchIncentivePlan) return
    refetch().then(response => {
      const paymentMethodsExists =
        response.data?.results && response.data.results.length > 0
      if (!paymentMethodsExists) {
        setShowNoPaymentInfo(true)
        return
      }
      publish.mutate(
        {
          organizationId,
          workspaceId,
          incentivePlanId: launchIncentivePlan,
        },
        {
          onSuccess: () =>
            toast('success', 'pmportal.incentives.launchSuccess'),
          onError: () => toast('error', 'pmportal.incentives.launchError'),
        }
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launchIncentivePlan])

  if (status === 'loading') {
    return <Loading />
  }

  if (status === 'error') {
    // TODO: handle error state
    return <span>Something went wrong!</span>
  }

  return (
    <FullWidthPage heading={formatMessage({ id: 'pmportal.incentives.title' })}>
      <NoPaymentMethodModal
        isOpen={showNoPaymentInfo}
        onCancel={() => setShowNoPaymentInfo(false)}
        onConfirm={() =>
          history.push(`/workspaces/${workspaceId}/settings/billing`)
        }
      />
      <EditableSection
        title={formatMessage({ id: 'pmportal.incentives.programs' })}
        action={
          <Button
            colorScheme="transparent"
            variant="outline"
            leftIcon={<Icon as={MdAdd} w="18px" h="18px" />}
            isLoading={create.isLoading}
            onClick={() => {
              create.mutate(
                {
                  organizationId,
                  workspaceId,
                  incentivePlanConfiguration: {},
                },
                {
                  onSuccess: data => {
                    const incentivePlanId = data.metadata?.id
                    if (!incentivePlanId) {
                      // This should never happen - metadata should be a required field i.e. never undefined
                      toast('error', 'pmportal.incentives.genericTryAgain')
                    } else {
                      history.push(`incentives/${incentivePlanId}`)
                    }
                  },
                }
              )
            }}
          >
            {formatMessage({ id: 'pmportal.incentives.newButton' })}
          </Button>
        }
        mt={6}
      >
        <IncentivePlansTable
          incentivePlans={data?.results}
          onLaunch={incentivePlanId => setLaunchIncentivePlan(incentivePlanId)}
          onRelaunch={incentivePlanId => {
            publish.mutate(
              {
                organizationId,
                workspaceId,
                incentivePlanId,
              },
              {
                onSuccess: () =>
                  toast('success', 'pmportal.incentives.relaunchSuccess'),
                onError: () =>
                  toast('error', 'pmportal.incentives.relaunchError'),
              }
            )
          }}
          onPause={incentivePlanId => {
            pause.mutate(
              {
                organizationId,
                workspaceId,
                incentivePlanId,
              },
              {
                onSuccess: () =>
                  toast('success', 'pmportal.incentives.pauseSuccess'),
                onError: () =>
                  toast('error', 'pmportal.incentives.genericTryAgain'),
              }
            )
          }}
          onResume={incentivePlanId => {
            resume.mutate(
              {
                organizationId,
                workspaceId,
                incentivePlanId,
              },
              {
                onSuccess: () =>
                  toast('success', 'pmportal.incentives.resumeSuccess'),
                onError: () =>
                  toast('error', 'pmportal.incentives.genericTryAgain'),
              }
            )
          }}
          onDelete={incentivePlanId => {
            setDeleteTargetId(incentivePlanId)
            onDeleteOpen()
          }}
        />
        <Pagination
          rowsPerPage={rowsPerPage}
          hasNext={false}
          hasPrev={false}
          onRowsPerPageChange={e => setRowsPerPage(Number(e.target.value))}
          onPrev={() => {
            setPaginationTokenOption({
              previousPageToken: data?.paginationToken,
            })
          }}
          onNext={() => {
            setPaginationTokenOption({ nextPageToken: data?.paginationToken })
          }}
        />
      </EditableSection>
      <IncentiveDeleteModal
        incentivePlanId={deleteTargetId}
        isLoading={remove.isLoading}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={incentivePlanId => {
          remove.mutate(
            {
              organizationId,
              workspaceId,
              incentivePlanId,
            },
            {
              onSuccess: () => {
                onDeleteClose()
                toast('success', 'pmportal.incentives.deleteSuccess')
              },
              onError: () =>
                toast('error', 'pmportal.incentives.genericTryAgain'),
            }
          )
        }}
      />
    </FullWidthPage>
  )
}

export default Incentives
